import React, { useState } from 'react';
import { Card } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { IClientContactData } from '../../../types/contacts/IClientContact';
import { ClientOrderContactsTable } from '../../../components/order/contacts/clientOrderContacts/ClientOrderContactsTable';
import { AddClientOrderContactForm } from '../../../components/order/contacts/clientOrderContacts/AddClientOrderContactForm';
import { EditClientOrderContactForm } from '../../../components/order/contacts/clientOrderContacts/EditClientOrderContactForm';
import { IFetchDataState } from '../../../store/IFetchDataState';
import { ContactsPageTemplate } from './subComponents/ContactsPageTemplate';
import { IClientUserData } from '../../../types/clients';
import { useListContactFunctionsQuery } from '../../../API/contacts/ContactFunctionsApi';
import { Show } from '../../../components/shared/Show';

export interface IContactsPageProps {
  loading: boolean;
  addFetchError?: Error;
  addFetchStatus: IFetchStatus;
  updateFetchError?: Error;
  updateFetchStatus: IFetchStatus;
  deleteFetchError?: Error;
  deleteFetchStatus: IFetchStatus;
  onDeleteContact: (contactId: string) => void;
  onResetAddContact: () => void;
  onResetUpdateContact: () => void;
  onResetDeleteContact: () => void;
}

interface IClientContactsPageProps extends IContactsPageProps {
  clientContacts: IClientContactData[];
  preExistingClientContacts: IFetchDataState<string, IClientUserData[]>;
  onAddNewContact: (data: IClientContactData) => void;
  onUpdateContact: (data: IClientContactData) => void;
}

export const ClientContactsPage: React.FC<IClientContactsPageProps> = ({
  loading,
  addFetchError,
  addFetchStatus,
  updateFetchError,
  updateFetchStatus,
  deleteFetchError,
  deleteFetchStatus,
  onResetAddContact,
  onResetUpdateContact,
  clientContacts,
  preExistingClientContacts,
  onAddNewContact,
  onUpdateContact,
  onDeleteContact,
  onResetDeleteContact,
}) => {
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [editContact, setEditContact] = useState<IClientContactData | null>(null);
  const { data: contactFunctions = [] } = useListContactFunctionsQuery();
  const onCancelAddContactForm = () => {
    setShowAddContactForm(false);
    onResetAddContact();
  };

  const onCancelEditContactForm = () => {
    setEditContact(null);
    onResetUpdateContact();
  };

  const handleAddContactForm = () => {
    setShowAddContactForm(true);
  };

  const onSelectExistingClient = (clientId: string) => {
    const existingContact = clientContacts.find((contact) => contact.clientUser && contact.clientUser.id === clientId);
    if (existingContact) {
      onCancelAddContactForm();
      setEditContact(existingContact);
    }
  };

  return (
    <Card>
      <ContactsPageTemplate
        hintText={'Points of contact for the client.'}
        header={'Client Contacts'}
        addContact={handleAddContactForm}
      >
        <Show when={contactFunctions.length !== 0}>
          <ClientOrderContactsTable
            data={clientContacts}
            loading={loading}
            onEditContact={setEditContact}
            contactFunctions={contactFunctions}
          />
        </Show>
      </ContactsPageTemplate>
      {showAddContactForm && (
        <AddClientOrderContactForm
          onCancel={onCancelAddContactForm}
          onSubmit={onAddNewContact}
          addFetchError={addFetchError}
          addFetchStatus={addFetchStatus}
          preExistingClientContacts={preExistingClientContacts}
          onSelectContact={onSelectExistingClient}
        />
      )}
      {editContact && (
        <EditClientOrderContactForm
          initialData={editContact}
          onSubmit={onUpdateContact}
          onCancel={onCancelEditContactForm}
          updateFetchStatus={updateFetchStatus}
          updateFetchError={updateFetchError}
          onDelete={onDeleteContact}
          deleteFetchStatus={deleteFetchStatus}
          deleteFetchError={deleteFetchError}
          onResetDeleteContact={onResetDeleteContact}
        />
      )}
    </Card>
  );
};
